import { useState, useMemo } from "react"
import WireframeLayout, { buildSidebarSections } from "@/components/WireframeLayout"
import { cn, Badge, Card, CardContent, Spark } from "../components/primitives"
import { WAREHOUSES, CHANNEL_META, SKU_DATA, generateDailySales, generateForecast } from "../components/data"
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart,
} from "recharts"

interface Props {
  warehouseId: string
  warehouseName: string
  channels: string[]
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n)
const fmtCurrency = (n: number) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : `$${(n / 1000).toFixed(0)}K`
const rangeDays = { "7d": 7, "30d": 30, "90d": 90 }

const statusColors = {
  healthy: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  critical: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  overstock: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200" },
  warning: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
}

const trendIcon = { growing: "trending_up", slowing: "trending_down", stable: "trending_flat" }
const trendColor = { growing: "text-emerald-600", slowing: "text-red-500", stable: "text-slate-400" }
const trendArrow = { growing: "\u2197", slowing: "\u2198", stable: "\u2192" }

type Filter = "all" | "critical" | "overstock" | "slowing" | "growing" | "restock"
type TimeRange = "7d" | "30d" | "90d"

// ─── Channel Pill ────────────────────────────────────────────────────────────

const ChannelPill = ({ name }: { name: string }) => {
  const m = CHANNEL_META[name] || CHANNEL_META.DTC
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border", m.bg, m.text, m.border)}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: m.dot, display: "inline-block", flexShrink: 0 }} />
      {name}
    </span>
  )
}

// ─── Channel Bar ─────────────────────────────────────────────────────────────

const ChannelBar = ({ split, colors }: { split: Record<string, number>; colors: Record<string, string> }) => (
  <div className="flex rounded-full overflow-hidden h-2.5 w-full">
    {Object.entries(split).map(([ch, pct]) => (
      <div key={ch} title={`${ch}: ${pct}%`} style={{ width: `${pct}%`, background: colors[ch] || "#94a3b8" }} />
    ))}
  </div>
)

// ─── KPI Card ────────────────────────────────────────────────────────────────

const KPICard = ({
  label, value, color = "text-slate-900", accent, onClick, active,
}: {
  label: string; value: string | number; color?: string; accent?: string; onClick?: () => void; active?: boolean
}) => (
  <button
    onClick={onClick}
    className={cn(
      "flex-1 min-w-[130px] rounded-xl border bg-white p-4 text-left transition-all cursor-pointer",
      active ? "ring-2 ring-blue-500 border-blue-300 shadow-md" : "border-slate-200 shadow-sm hover:shadow-md",
    )}
  >
    <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">{label}</div>
    <div className={cn("text-xl font-bold", accent || color)}>{value}</div>
  </button>
)

// ─── Mini channel bar for table rows ─────────────────────────────────────────

const MiniChannelBar = ({ channelStock }: { channelStock: Record<string, number> }) => {
  const total = Object.values(channelStock).reduce((a, b) => a + b, 0)
  if (total === 0) return null
  return (
    <div className="flex rounded-full overflow-hidden h-1.5 w-full min-w-[60px]">
      {Object.entries(channelStock).map(([ch, units]) => (
        <div
          key={ch}
          title={`${ch}: ${units.toLocaleString()}`}
          style={{ width: `${(units / total) * 100}%`, background: CHANNEL_META[ch]?.color || "#94a3b8" }}
        />
      ))}
    </div>
  )
}

// ─── Chart Tooltip ───────────────────────────────────────────────────────────

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2.5 text-xs">
      <div className="font-medium text-slate-600 mb-1">{label}</div>
      {payload.map((p: any) => (
        p.dataKey !== "lower" && p.dataKey !== "band" && (
          <div key={p.dataKey} className="flex items-center gap-1.5">
            <span style={{ width: 8, height: 3, borderRadius: 2, background: p.color, display: "inline-block" }} />
            <span className="text-slate-500 capitalize">{p.dataKey}:</span>
            <span className="font-semibold text-slate-800">{p.value?.toLocaleString()}</span>
          </div>
        )
      ))}
    </div>
  )
}

// ─── Sales & Forecast Chart ─────────────────────────────────────────────────

const SalesChart = ({
  skuId, channelStock, trend, timeRange, height = 220,
}: {
  skuId: string; channelStock: Record<string, number>; trend: string; timeRange: TimeRange; height?: number
}) => {
  const data = useMemo(() => {
    const channels = Object.keys(channelStock)
    const days = rangeDays[timeRange]
    const histMap: Record<string, number> = {}
    const fcMap: Record<string, { units: number; upper: number; lower: number }> = {}

    channels.forEach((ch) => {
      generateDailySales(skuId, ch, days, trend).forEach((d) => {
        histMap[d.date] = (histMap[d.date] || 0) + d.units
      })
      generateForecast(skuId, ch, days, trend).forEach((d) => {
        if (!fcMap[d.date]) fcMap[d.date] = { units: 0, upper: 0, lower: 0 }
        fcMap[d.date].units += d.units
        fcMap[d.date].upper += d.upper
        fcMap[d.date].lower += d.lower
      })
    })

    const combined: any[] = []
    Object.entries(histMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([date, units]) => combined.push({ date, sales: units }))

    // bridge: last history point also gets forecast value for continuity
    const lastHist = combined[combined.length - 1]
    const fcEntries = Object.entries(fcMap).sort(([a], [b]) => a.localeCompare(b))
    if (lastHist && fcEntries.length) {
      lastHist.forecast = lastHist.sales
    }

    fcEntries.forEach(([date, { units, upper, lower }]) => {
      combined.push({ date, forecast: units, upper, lower, band: upper - lower })
    })
    return combined
  }, [skuId, channelStock, trend, timeRange])

  const formatXTick = (d: string) => {
    const parts = d.split("-")
    return `${parts[1]}/${parts[2]}`
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={formatXTick} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 9 }} width={40} />
        <Tooltip content={<ChartTooltip />} />
        {/* Confidence band */}
        <Area dataKey="lower" stackId="band" fill="transparent" stroke="none" />
        <Area dataKey="band" stackId="band" fill="#3b82f6" fillOpacity={0.08} stroke="none" />
        {/* Past sales */}
        <Area dataKey="sales" fill="#3b82f6" fillOpacity={0.12} stroke="#3b82f6" strokeWidth={2} dot={false} connectNulls={false} />
        {/* Forecast */}
        <Line dataKey="forecast" stroke="#3b82f6" strokeDasharray="6 3" strokeWidth={2} dot={false} connectNulls={false} />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

// ─── Per-Channel Chart ───────────────────────────────────────────────────────

const ChannelSalesChart = ({
  skuId, channelStock, trend, timeRange, height = 180,
}: {
  skuId: string; channelStock: Record<string, number>; trend: string; timeRange: TimeRange; height?: number
}) => {
  const { data, channels } = useMemo(() => {
    const chs = Object.keys(channelStock)
    const days = rangeDays[timeRange]
    const dateMap: Record<string, Record<string, number>> = {}

    chs.forEach((ch) => {
      generateDailySales(skuId, ch, days, trend).forEach((d) => {
        if (!dateMap[d.date]) dateMap[d.date] = {}
        dateMap[d.date][ch] = d.units
      })
    })

    const rows = Object.entries(dateMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, vals]) => ({ date, ...vals }))
    return { data: rows, channels: chs }
  }, [skuId, channelStock, trend, timeRange])

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d) => d.split("-").slice(1).join("/")} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 9 }} width={40} />
        <Tooltip content={<ChartTooltip />} />
        {channels.map((ch) => (
          <Line key={ch} dataKey={ch} stroke={CHANNEL_META[ch]?.color || "#94a3b8"} strokeWidth={2} dot={false} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  )
}

// ─── Time Range Selector ─────────────────────────────────────────────────────

const TimeRangeSelector = ({ value, onChange }: { value: TimeRange; onChange: (v: TimeRange) => void }) => (
  <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
    {(["7d", "30d", "90d"] as TimeRange[]).map((r) => (
      <button
        key={r}
        onClick={() => onChange(r)}
        className={cn(
          "px-3 py-1 text-xs font-medium transition-colors cursor-pointer",
          value === r ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50",
        )}
      >
        {r}
      </button>
    ))}
  </div>
)

// ─── Slide-out Panel ─────────────────────────────────────────────────────────

const SKUSlidePanel = ({
  sku, warehouseId, timeRange, onTimeRangeChange, onClose,
}: {
  sku: any; warehouseId: string; timeRange: TimeRange; onTimeRangeChange: (v: TimeRange) => void; onClose: () => void
}) => {
  const channels = Object.keys(sku.channelStock)
  const totalStock = Object.values(sku.channelStock as Record<string, number>).reduce((a, b) => a + b, 0)
  const sc = statusColors[sku.status] || statusColors.healthy

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />

      {/* Panel */}
      <div
        className="fixed top-0 right-0 h-full bg-white border-l border-slate-200 shadow-2xl z-50 overflow-y-auto"
        style={{ width: 480 }}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-5">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-base font-bold text-slate-900 truncate">{sku.name}</h2>
                <Badge variant={sku.status}>{sku.status}</Badge>
              </div>
              <div className="text-xs text-slate-400">{sku.id}</div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg cursor-pointer p-1">&times;</button>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            <div className="text-center p-2 rounded-lg bg-slate-50">
              <div className="text-[10px] text-slate-400 uppercase">Stock</div>
              <div className="text-sm font-bold text-slate-900">{totalStock.toLocaleString()}</div>
            </div>
            <div className="text-center p-2 rounded-lg bg-slate-50">
              <div className="text-[10px] text-slate-400 uppercase">Velocity</div>
              <div className="text-sm font-bold text-slate-900">{sku.velocity}/d</div>
            </div>
            <div className="text-center p-2 rounded-lg bg-slate-50">
              <div className="text-[10px] text-slate-400 uppercase">DOS</div>
              <div className="text-sm font-bold text-slate-900">{sku.dos}d</div>
            </div>
            <div className="text-center p-2 rounded-lg bg-slate-50">
              <div className="text-[10px] text-slate-400 uppercase">Trend</div>
              <div className={cn("text-sm font-bold", trendColor[sku.demandTrend])}>
                {trendArrow[sku.demandTrend]} {sku.demandTrend}
              </div>
            </div>
          </div>

          {/* Chart section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider">Sales & Forecast</h3>
              <TimeRangeSelector value={timeRange} onChange={onTimeRangeChange} />
            </div>
            <SalesChart skuId={sku.id} channelStock={sku.channelStock} trend={sku.demandTrend} timeRange={timeRange} height={200} />
            <div className="flex items-center gap-4 mt-2 text-[10px] text-slate-400">
              <span className="flex items-center gap-1">
                <span style={{ width: 14, height: 2, background: "#3b82f6", display: "inline-block", borderRadius: 1 }} /> Past sales
              </span>
              <span className="flex items-center gap-1">
                <span style={{ width: 14, height: 0, borderTop: "2px dashed #3b82f6", display: "inline-block" }} /> Forecast
              </span>
            </div>
          </div>

          {/* Channel breakdown */}
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-3">Channel Performance</h3>
            <div className="space-y-2">
              {channels.map((ch) => {
                const stock = sku.channelStock[ch]
                const pct = Math.round((stock / totalStock) * 100)
                const restock = sku.restockDays?.[ch]
                const meta = CHANNEL_META[ch] || CHANNEL_META.DTC
                return (
                  <div key={ch} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50">
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: meta.color, flexShrink: 0 }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-700">{ch}</span>
                        <span className="text-xs font-bold text-slate-900">{stock.toLocaleString()} units</span>
                      </div>
                      <div className="flex items-center justify-between mt-0.5">
                        <span className="text-[10px] text-slate-400">{pct}% of stock</span>
                        {restock && (
                          <span className={cn("text-[10px] font-medium", restock < 14 ? "text-red-500" : restock < 30 ? "text-amber-500" : "text-slate-400")}>
                            {restock}d to restock
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Per-channel sales chart */}
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-3">Channel Sales Comparison</h3>
            <ChannelSalesChart skuId={sku.id} channelStock={sku.channelStock} trend={sku.demandTrend} timeRange={timeRange} height={160} />
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {channels.map((ch) => (
                <span key={ch} className="flex items-center gap-1 text-[10px] text-slate-400">
                  <span style={{ width: 10, height: 2, borderRadius: 1, background: CHANNEL_META[ch]?.color, display: "inline-block" }} />
                  {ch}
                </span>
              ))}
            </div>
          </div>

          {/* Open full page link */}
          <a
            href={`/wireframes/inventory/warehouse/${warehouseId}/sku/${sku.id}`}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Open full detail page &rarr;
          </a>
        </div>
      </div>
    </>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function WarehouseDetail({ warehouseId, warehouseName, channels }: Props) {
  const [filter, setFilter] = useState<Filter>("all")
  const [selectedSku, setSelectedSku] = useState<any>(null)
  const [timeRange, setTimeRange] = useState<TimeRange>("30d")
  const [sortBy, setSortBy] = useState<string>("name")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc")

  const warehouse = WAREHOUSES.find((w) => w.id === warehouseId)
  if (!warehouse) {
    return (
      <WireframeLayout title={warehouseName} titleIcon="🏭" sidebarLinks={buildSidebarSections(null, "inventory-data")}>
        <div className="flex items-center justify-center min-h-[400px] text-slate-400">Warehouse not found</div>
      </WireframeLayout>
    )
  }

  const skus = warehouse.skus

  // KPI counts
  const slowingCount = skus.filter((s) => s.demandTrend === "slowing").length
  const growingCount = skus.filter((s) => s.demandTrend === "growing").length
  const restockCount = skus.filter((s) => s.status === "critical" || s.status === "warning").length

  // Filtering
  const filteredSkus = skus.filter((s) => {
    if (filter === "all") return true
    if (filter === "critical") return s.status === "critical"
    if (filter === "overstock") return s.status === "overstock"
    if (filter === "slowing") return s.demandTrend === "slowing"
    if (filter === "growing") return s.demandTrend === "growing"
    if (filter === "restock") return s.status === "critical" || s.status === "warning"
    return true
  })

  // Sorting
  const sortedSkus = [...filteredSkus].sort((a, b) => {
    let cmp = 0
    if (sortBy === "name") cmp = a.name.localeCompare(b.name)
    else if (sortBy === "stock") cmp = a.stock - b.stock
    else if (sortBy === "velocity") cmp = (a.velocity || 0) - (b.velocity || 0)
    else if (sortBy === "dos") cmp = (a.dos || 0) - (b.dos || 0)
    return sortDir === "desc" ? -cmp : cmp
  })

  const handleSort = (col: string) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc")
    else { setSortBy(col); setSortDir("asc") }
  }

  const sortArrow = (col: string) => sortBy === col ? (sortDir === "asc" ? " \u25B4" : " \u25BE") : ""

  const filters: { key: Filter; label: string; count?: number; accent?: string }[] = [
    { key: "all", label: "All SKUs", count: skus.length },
    { key: "critical", label: "Critical", count: skus.filter((s) => s.status === "critical").length, accent: "text-red-600" },
    { key: "overstock", label: "Overstock", count: skus.filter((s) => s.status === "overstock").length, accent: "text-violet-600" },
    { key: "slowing", label: "Slowing", count: slowingCount, accent: "text-amber-600" },
    { key: "growing", label: "Growing", count: growingCount, accent: "text-emerald-600" },
    { key: "restock", label: "Need Restock", count: restockCount, accent: "text-red-600" },
  ]

  return (
    <WireframeLayout
      title={warehouseName}
      subtitle={channels.join(" · ")}
      titleIcon="🏭"
      sidebarLinks={buildSidebarSections(null, "inventory-data")}
    >
      <div className="flex flex-col gap-6">
        {/* Back link */}
        <div>
          <a href="/wireframes/inventory/with-data" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors" style={{ textDecoration: "none" }}>
            &larr; Back to warehouses
          </a>
        </div>

        {/* Channel Distribution */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Channel Distribution</span>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(warehouse.channelSplit).map(([ch, pct]) => (
                <span key={ch} className="flex items-center gap-1 text-[10px] text-slate-500">
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: warehouse.channelColors[ch], display: "inline-block" }} />
                  {ch} {pct}%
                </span>
              ))}
            </div>
          </div>
          <ChannelBar split={warehouse.channelSplit} colors={warehouse.channelColors} />
        </div>

        {/* Filter Chips */}
        <div className="flex gap-2 flex-wrap">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer border",
                filter === f.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300",
              )}
            >
              {f.label}
              {f.count !== undefined && (
                <span className={cn(
                  "text-[10px] font-bold",
                  filter === f.key ? "text-slate-300" : (f.accent || "text-slate-400"),
                )}>
                  {f.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* SKU Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  {[
                    { key: "name", label: "SKU", align: "left" },
                    { key: "stock", label: "Stock", align: "right" },
                    { key: "status", label: "Status", align: "center" },
                    { key: "velocity", label: "Velocity", align: "right" },
                    { key: "dos", label: "DOS", align: "right" },
                    { key: "channels", label: "Channels", align: "center" },
                    { key: "trend", label: "Trend", align: "center" },
                  ].map((col) => (
                    <th
                      key={col.key}
                      onClick={() => ["name", "stock", "velocity", "dos"].includes(col.key) ? handleSort(col.key) : undefined}
                      className={cn(
                        "px-4 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider",
                        col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left",
                        ["name", "stock", "velocity", "dos"].includes(col.key) && "cursor-pointer hover:text-slate-600 select-none",
                      )}
                    >
                      {col.label}{sortArrow(col.key)}
                    </th>
                  ))}
                  <th className="px-4 py-3 w-8" />
                </tr>
              </thead>
              <tbody>
                {sortedSkus.map((sku) => {
                  const sc = statusColors[sku.status] || statusColors.healthy
                  const sparkData = (() => {
                    const chs = Object.keys(sku.channelStock)
                    const dayMap: Record<string, number> = {}
                    chs.forEach((ch) => {
                      generateDailySales(sku.id, ch, 14, sku.demandTrend).forEach((d) => {
                        dayMap[d.date] = (dayMap[d.date] || 0) + d.units
                      })
                    })
                    return Object.entries(dayMap).sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v)
                  })()

                  return (
                    <tr
                      key={sku.id}
                      onClick={() => setSelectedSku(sku)}
                      className={cn(
                        "border-b border-slate-50 cursor-pointer transition-colors",
                        selectedSku?.id === sku.id ? "bg-blue-50" : "hover:bg-slate-50",
                      )}
                    >
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-slate-900">{sku.name}</div>
                        <div className="text-[10px] text-slate-400">{sku.id}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-semibold text-slate-900">{sku.stock.toLocaleString()}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={sku.status}>{sku.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-medium text-slate-700">{sku.velocity}</span>
                        <span className="text-[10px] text-slate-400">/day</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={cn("text-sm font-medium", sku.dos < 14 ? "text-red-600" : sku.dos > 120 ? "text-violet-600" : "text-slate-700")}>
                          {sku.dos}d
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="w-24 mx-auto">
                          <MiniChannelBar channelStock={sku.channelStock} />
                          <div className="flex gap-1 mt-1 justify-center flex-wrap">
                            {Object.keys(sku.channelStock).map((ch) => (
                              <span key={ch} className="text-[8px] text-slate-400">{ch}</span>
                            ))}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <Spark data={sparkData} color={sku.demandTrend === "growing" ? "#10b981" : sku.demandTrend === "slowing" ? "#ef4444" : "#94a3b8"} w={56} h={18} />
                          <span className={cn("text-[10px] font-medium", trendColor[sku.demandTrend])}>
                            {trendArrow[sku.demandTrend]} {sku.demandTrend}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <a
                          href={`/wireframes/inventory/warehouse/${warehouseId}/sku/${sku.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-slate-300 hover:text-slate-600 transition-colors"
                          title="Open full detail"
                        >
                          &#x2197;
                        </a>
                      </td>
                    </tr>
                  )
                })}
                {sortedSkus.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-400">No SKUs match this filter</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Slide-out Panel */}
      {selectedSku && (
        <SKUSlidePanel
          sku={selectedSku}
          warehouseId={warehouseId}
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          onClose={() => setSelectedSku(null)}
        />
      )}
    </WireframeLayout>
  )
}
